import { create } from 'zustand'

import { LEVELS, type LevelConfig } from '@config'
import { buildNetwork, toggleSwitch, uncoupleWagon, type Network } from '@engine'

export enum GamePhase {
  LOADING,
  TITLE,
  CREDITS,
  TITLE_TRANSITION,
  LEVEL_SELECTION,
  LEVEL_SELECTION_TRANSITION,
  PLAYING,
  LEVEL_TRANSITION,
}

interface GameState {
  phase: GamePhase
  setPhase: (phase: GamePhase) => void

  paused: boolean
  setPaused: (paused: boolean) => void

  level: LevelConfig
  currentLevelIndex: number

  forceTransition?: boolean
  nextLevelIndex?: number
  setNextLevelIndex: (index: number, force?: boolean) => void

  nextLevel: () => void
  resetLevel: () => void

  levelComplete: boolean
  setLevelComplete: (value: boolean) => void

  network: Network
  handleToggleSwitch: (x: number, y: number) => void
  handleUncoupleWagon: (wagonId: string) => void
}

function getLevel(
  currentLevelIndex: number,
  goalSequence?: string[],
): Pick<GameState, 'level' | 'network' | 'currentLevelIndex'> {
  const baseLevel = LEVELS[currentLevelIndex] || LEVELS[LEVELS.length - 1]
  const level: LevelConfig = {
    ...baseLevel,
    goal: {
      ...baseLevel.goal,
      sequence: baseLevel.goal.generator
        ? (goalSequence ?? baseLevel.goal.generator())
        : baseLevel.goal.sequence,
    },
  }
  const network = buildNetwork(level.grid)

  return { currentLevelIndex, level, network }
}

const START_LEVEL = getLevel(0)

export const useGameStore = create<GameState>((set, get) => ({
  phase: GamePhase.LOADING,
  setPhase: phase => set({ phase }),

  paused: true,
  setPaused: paused => set({ paused }),

  level: START_LEVEL.level,
  currentLevelIndex: START_LEVEL.currentLevelIndex,

  setNextLevelIndex: (nextLevelIndex, force = false) => {
    set({ nextLevelIndex, forceTransition: force })
  },

  nextLevel: () => {
    const nextLevel = get().nextLevelIndex || get().currentLevelIndex + 1

    set({
      phase:
        nextLevel === 0
          ? GamePhase.TITLE
          : nextLevel === 1
            ? GamePhase.LEVEL_SELECTION
            : GamePhase.PLAYING,
      nextLevelIndex: undefined,
      forceTransition: false,
      levelComplete: false,
      ...getLevel(nextLevel),
    })
  },
  resetLevel: () => {
    const { currentLevelIndex, level } = get()

    set({
      phase: GamePhase.PLAYING,
      nextLevelIndex: undefined,
      forceTransition: false,
      levelComplete: false,
      ...getLevel(currentLevelIndex, level.goal.sequence),
    })
  },

  levelComplete: false,
  setLevelComplete: value => set({ levelComplete: value }),

  network: START_LEVEL.network,
  handleToggleSwitch: (x, y) => {
    const { network } = get()
    const didToggle = toggleSwitch(network, x, y)
    if (!didToggle) return

    set(state => ({ network: { ...state.network! } }))
  },
  handleUncoupleWagon: wagonId => {
    const { network } = get()
    const didUncouple = uncoupleWagon(network, wagonId)
    if (!didUncouple) return

    set(state => ({ network: { ...state.network! } }))
  },
}))
